import { useState, useEffect } from 'react';
import i18n from 'i18next';
import useIsMobile from '../hooks/useIsMobile';

const BACKEND_URL = import.meta.env.VITE_API_URL || 'https://logoilab.com/api';

const fonts = {
  mono: "'JetBrains Mono', 'Fira Code', monospace",
  sans: "'Inter', -apple-system, sans-serif",
};

export default function TabAIGuide({ panel, data, accent, autoExpand = false, aiConsent = false, onRequestConsent }) {
  const mobile = useIsMobile();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(true);
  const [autoTriggered, setAutoTriggered] = useState(false);

  const cacheKey = `logos_guide_${panel}`;

  useEffect(() => {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        const p = JSON.parse(cached);
        if (Date.now() - p.ts < 600000) { setResult(p.result); setCollapsed(false); }
      } catch {}
    }
  }, []);

  useEffect(() => {
    if (autoExpand && aiConsent && !result && !loading && !autoTriggered) {
      setAutoTriggered(true);
      const timer = setTimeout(() => generate(), 800);
      return () => clearTimeout(timer);
    }
  }, [autoExpand, aiConsent, result, loading, autoTriggered]);

  const generate = async () => {
    if (!aiConsent) {
      if (onRequestConsent) onRequestConsent();
      return;
    }
    setLoading(true); setError(null); setCollapsed(false);
    try {
      const resp = await fetch(`${BACKEND_URL}/protocol/interpret-panel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ panel, data, language: i18n.language || 'es' }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = await resp.json();
      if (json.result) {
        setResult(json.result);
        sessionStorage.setItem(cacheKey, JSON.stringify({ result: json.result, ts: Date.now() }));
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const ac = accent || '#C084FC';

  return (
    <div style={{
      background: `linear-gradient(135deg, ${ac}08 0%, rgba(6,182,212,0.03) 100%)`,
      border: `1px solid ${ac}25`, borderRadius: 12, overflow: 'hidden',
      marginBottom: 16, transition: 'all 0.3s ease',
    }}>
      {/* Header bar — always visible */}
      <div
        onClick={() => result && setCollapsed(!collapsed)}
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '8px 14px', cursor: result ? 'pointer' : 'default',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14 }}>🧠</span>
          <span style={{ fontFamily: fonts.mono, fontSize: 9, letterSpacing: '0.12em', color: ac }}>
            GUÍA AI
          </span>
          {result && (
            <span style={{ fontFamily: fonts.mono, fontSize: 8, color: '#8B85A0', marginLeft: 4 }}>
              {collapsed ? '▸ expandir' : '▾ colapsar'}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {loading && (
            <div style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(192,132,252,0.2)', borderTopColor: ac, animation: 'spin 1s linear infinite' }} />
          )}
          <button onClick={(e) => { e.stopPropagation(); generate(); }} disabled={loading} style={{
            background: loading ? `${ac}15` : `linear-gradient(135deg, ${ac}, #818CF8)`,
            border: 'none', borderRadius: 6, padding: '4px 10px', color: '#fff',
            fontFamily: fonts.mono, fontSize: 8, cursor: loading ? 'wait' : 'pointer',
            opacity: loading ? 0.5 : 1, letterSpacing: '0.05em',
          }}>
            {loading ? '...' : result ? '↻' : '✦ Interpretar'}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && <div style={{ padding: '4px 14px 8px', fontSize: 9, color: '#EF4444', fontFamily: fonts.mono }}>Error: {error}</div>}

      {/* Content — collapsible */}
      {result && !collapsed && (
        <div style={{ padding: '0 14px 12px', display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap: 10 }}>
          {result.explanation && (
            <div style={{ background: `${ac}08`, borderRadius: 8, padding: 10, borderLeft: `2px solid ${ac}` }}>
              <div style={{ fontFamily: fonts.mono, fontSize: 7, color: ac, marginBottom: 3, letterSpacing: '0.1em' }}>QUÉ SIGNIFICA</div>
              <p style={{ fontFamily: fonts.sans, fontSize: 11, color: '#E8E4F0', margin: 0, lineHeight: 1.55 }}>{result.explanation}</p>
            </div>
          )}
          {result.howToUse && (
            <div style={{ background: 'rgba(6,182,212,0.06)', borderRadius: 8, padding: 10, borderLeft: '2px solid #06B6D4' }}>
              <div style={{ fontFamily: fonts.mono, fontSize: 7, color: '#06B6D4', marginBottom: 3, letterSpacing: '0.1em' }}>CÓMO USAR</div>
              <p style={{ fontFamily: fonts.sans, fontSize: 11, color: '#E8E4F0', margin: 0, lineHeight: 1.55 }}>{result.howToUse}</p>
            </div>
          )}
          {result.insight && (
            <div style={{ background: 'rgba(245,197,66,0.06)', borderRadius: 8, padding: 10, borderLeft: '2px solid #F5C542' }}>
              <div style={{ fontFamily: fonts.mono, fontSize: 7, color: '#F5C542', marginBottom: 3, letterSpacing: '0.1em' }}>💡 INSIGHT</div>
              <p style={{ fontFamily: fonts.sans, fontSize: 11, color: '#E8E4F0', margin: 0, lineHeight: 1.55, fontWeight: 500 }}>{result.insight}</p>
            </div>
          )}
          {result.action && (
            <div style={{ background: 'rgba(16,185,129,0.06)', borderRadius: 8, padding: 10, borderLeft: '2px solid #10B981' }}>
              <div style={{ fontFamily: fonts.mono, fontSize: 7, color: '#10B981', marginBottom: 3, letterSpacing: '0.1em' }}>⚡ ACCIÓN</div>
              <p style={{ fontFamily: fonts.sans, fontSize: 11, color: '#E8E4F0', margin: 0, lineHeight: 1.55 }}>{result.action}</p>
            </div>
          )}
        </div>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
